import * as React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { reconnectDelay } from "@/api/connectivity";
import { useAppVersion } from "@/api/hooks";
import { serverStatusFrom } from "@/api/serverStatus";
import { useBrowserOnline, usePageVisible } from "@/hooks/useBrowserPresence";
import { maintenanceNote } from "@/lib/maintenanceNote";

/** The slice of the ``useMe()`` result the screen drives. */
export type ReconnectProbe = {
  refetch: () => unknown;
  isFetching: boolean;
  errorUpdateCount: number;
};

/** Ask ``/auth/me`` again after each failed attempt, backing off, for as
 *  long as the screen is mounted. The first successful answer re-renders
 *  the page behind it — no reload, no sign-in.
 *
 *  Not while the tab is hidden: nobody is looking, and every open tab
 *  asking every 10 s through a long outage is load the backend meets as
 *  it comes back. Back in view it asks at once and backs off afresh. */
function useReconnectLoop(probe: ReconnectProbe): () => void {
  const { refetch, isFetching, errorUpdateCount } = probe;
  const visible = usePageVisible();
  const attempt = React.useRef(0);
  React.useEffect(() => {
    // Hidden, or an attempt (with the probe's own retries) still running.
    if (!visible || isFetching) return;
    const id = window.setTimeout(() => {
      attempt.current += 1;
      void refetch();
    }, reconnectDelay(attempt.current));
    return () => window.clearTimeout(id);
  }, [visible, isFetching, errorUpdateCount, refetch]);
  const retryNow = React.useCallback(() => {
    attempt.current = 0;
    void refetch();
  }, [refetch]);
  const wasVisible = React.useRef(visible);
  React.useEffect(() => {
    const cameBack = visible && !wasVisible.current;
    wasVisible.current = visible;
    if (cameBack) retryNow();
  }, [visible, retryNow]);
  return retryNow;
}

/** Full-page state for a signed-in page that has no user yet because the
 *  backend is unreachable — typically a deploy restarting it. Replaces the
 *  old bounce to /login, which a restart must never cause. */
export function ReconnectingScreen({ probe }: { probe: ReconnectProbe }): React.JSX.Element {
  const { t } = useTranslation();
  const retryNow = useReconnectLoop(probe);
  // No network in the browser: that is the user's connection, not a
  // restart (the probe waits for it — TanStack pauses offline queries).
  const online = useBrowserOnline();
  // The last maintenance notice the server-status poll saw, if any — a
  // deploy announces itself before the backend goes away.
  const { maintenance } = serverStatusFrom(useAppVersion());
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Stack
        role="status"
        aria-live="polite"
        sx={{ alignItems: "center", gap: 2, maxWidth: 480, textAlign: "center" }}
      >
        <CircularProgress aria-hidden />
        <Typography variant="h5" component="h1">
          {online ? t("maintenance.restarting_title") : t("maintenance.offline_title")}
        </Typography>
        <Typography color="text.secondary">
          {online ? t("maintenance.restarting_body") : t("maintenance.offline_body")}
        </Typography>
        {maintenance?.enabled && (
          <Alert severity="warning" variant="outlined" sx={{ textAlign: "left", width: "100%" }}>
            <AlertTitle>{t("maintenance.title")}</AlertTitle>
            {maintenance.message
              ? maintenanceNote(t, maintenance.message)
              : t("maintenance.default_body")}
          </Alert>
        )}
        <Button variant="outlined" onClick={retryNow} disabled={probe.isFetching}>
          {t("maintenance.retry_now")}
        </Button>
      </Stack>
    </Box>
  );
}
